import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { AuthService } from './auth.service';
import { SiteCapabilities, SiteConfigService } from './site-config.service';

export function capabilityForPath(path: string): keyof SiteCapabilities | null {
  const normalized = `/${path.split('?')[0].split('/').filter(Boolean).filter((part) => !/^[a-z]{2}(?:-[a-z]{2})?$/i.test(part)).join('/')}`;
  if (/^\/(?:giris-yap|kayit-ol|sifremi-unuttum|login|register|forgot-password|profil|iletisim-guncelle|sifre-guncelle|yorumlarim|son-gezdiklerim|takip-ettiklerim|bildirimler|fallarim|siparislerim|ucretsiz-fal|ucretsiz-fallar|free-fortune|free-fortunes)(?:\/|$)/i.test(normalized)) return 'auth';
  if (/^\/(?:falci-ol|satici-ol|become-reader|ser-vidente|devenir-voyant|berater-werden)(?:\/|$)/i.test(normalized)) return 'sellerOnboarding';
  if (/^\/seller-panel(?:\/|$)/i.test(normalized)) return 'sellerPanel';
  if (/^\/admin-panel(?:\/|$)/i.test(normalized)) return 'adminPanel';
  if (/^\/(?:falcilar|saticilar|falci|satici|readers|reader|berater|videntes|vidente|voyants|voyant)(?:\/|$)/i.test(normalized)) return 'marketplace';
  if (/^\/(?:sepet|cart|odeme|checkout|misafir-siparis|guest-order|gastbestellung|pedido-invitado|commande-invite)(?:\/|$)/i.test(normalized)) return 'cart';
  if (/^\/(?:fal|urunler|urun|fortune|wahrsagen|videncia|voyance|kategori|category|kategorie|categoria|categorie)(?:\/|$)/i.test(normalized)) return 'commerce';
  if (/^\/(?:akis|feed)(?:\/|$)/i.test(normalized)) return 'feeds';
  if (/^\/(?:canli|live)(?:\/|$)/i.test(normalized)) return 'live';
  if (/^\/(?:kredi-al|credits|guthaben|creditos)(?:\/|$)/i.test(normalized)) return 'wallet';
  if (/^\/(?:randevu|appointment)(?:\/|$)/i.test(normalized)) return 'appointments';
  if (/^(?:\/ekibimiz|\/team|\/clinicians|\/equipe)(?:\/|$)/i.test(normalized)) return 'team';
  if (/^(?:\/subeler|\/branches|\/clinics)(?:\/|$)/i.test(normalized)) return 'branches';
  if (/^(?:\/(?:hizmet|hizmetler|fiyat-listesi)|\/services|\/service|\/leistungen)(?:\/|$)/i.test(normalized)) return 'services';
  return null;
}

/** Resolves the least-privileged permission needed for an admin URL. */
export function adminPermissionForPath(path: string): string {
  const normalized = String(path || '').toLocaleLowerCase('tr-TR');
  if (!normalized.includes('/admin-panel')) return 'admin';
  if (normalized.includes('/fortune-journeys') || normalized.includes('/fal-akislari')) return 'admin';
  if (normalized.includes('/subeler') || normalized.includes('/branches')) return 'admin.subeler';
  if (normalized.includes('/personeller') || normalized.includes('/staff')) return 'admin.personeller';
  if (normalized.includes('/hizmetler') || normalized.includes('/services')) return 'admin.hizmetler';
  if (normalized.includes('/randevular') || normalized.includes('/appointments')) return 'admin.randevular';
  if (normalized.includes('/adisyonlar') || normalized.includes('/additions') || normalized.includes('/gelir-gider') || normalized.includes('/income-expense')) return 'admin.randevular';
  if (normalized.includes('/sosyal-medya') || normalized.includes('/social-providers')) return 'admin.sosyal';
  if (normalized.includes('/urun-sablonlari') || normalized.includes('/product-templates')) return 'admin.urunler';
  if (normalized.includes('/kategoriler') || normalized.includes('/categories')) return 'admin.kategoriler';
  if (normalized.includes('/users') || normalized.includes('/kullanicilar')) return 'admin.kullanicilar';
  if (normalized.includes('/sellers') || normalized.includes('/saticilar')) {
    if (normalized.includes('urun-skor') || normalized.includes('product-score')) return 'admin.urunler.skor';
    if (normalized.includes('editoryal') || normalized.includes('editorial')) return 'admin.urunler.editoryal';
    if (normalized.includes('basvuru') || normalized.includes('application')) return 'admin.saticilar.onayla';
    if (normalized.includes('/new') || normalized.includes('/yeni')) return 'admin.saticilar.yeni';
    return 'admin.saticilar';
  }
  if (normalized.includes('/orders') || normalized.includes('/siparis')) {
    return normalized.includes('refund') || normalized.includes('iade') ? 'admin.siparisler.iade' : 'admin.siparisler';
  }
  if (normalized.includes('/blogs') || normalized.includes('/bloglar')) return 'admin.bloglar';
  if (normalized.includes('/support') || normalized.includes('/destek')) return 'admin.destek';
  if (normalized.includes('/content') || normalized.includes('/icerik')) return 'admin.icerik';
  if (normalized.includes('/payments') || normalized.includes('/odemeler') || normalized.includes('/earnings') || normalized.includes('/kazanc')) return 'admin.odemeler';
  if (normalized.includes('/analytics') || normalized.includes('/analitik') || normalized.includes('/reports')) return 'admin.analitik';
  if (normalized.includes('/permissions') || normalized.includes('/izinler')) return 'admin.izinler';
  if (normalized.includes('/translations') || normalized.includes('/dil-cevirileri')) return 'admin.ceviriler';
  if (normalized.includes('/system') || normalized.includes('/sistem')) return 'admin.sistem';
  return 'admin';
}

export const siteRouteGuard: CanActivateFn = async (_route, state) => {
  const site = inject(SiteConfigService);
  const router = inject(Router);
  const auth = inject(AuthService);
  await site.ready();
  const capability = capabilityForPath(state.url);
  if (capability === 'adminPanel' && typeof window !== 'undefined') {
    // Admin access is store-scoped and must not rely on an SSR/stale auth
    // snapshot after a hard refresh. Force the current permission payload.
    await auth.refresh(true);
    // Keep the URL stable on direct loads. AdminPanelPage renders the sign-in or
    // permission state in place, so a missing client session must not bounce the
    // user through the public auth/home shell and lose the admin route.
  }
  const enabled = capability === 'cart' && site.config().capabilities.cart === undefined
    ? site.enabled('commerce')
    : !capability || site.enabled(capability);
  if (enabled) return true;
  if (typeof window !== 'undefined') {
    void fetch('/next/route-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: state.url })
    }).catch(() => undefined);
  }
  return router.parseUrl('/404');
};

/**
 * The localized route is intentionally a single `:locale` branch. It must
 * never act as a catch-all for an arbitrary first URL segment; otherwise a
 * typo such as /asd is interpreted as a locale and its empty child renders
 * the homepage after hydration.
 */
export const supportedLocaleRouteGuard: CanMatchFn = async (_route, segments) => {
  const site = inject(SiteConfigService);
  await site.ready();
  const value = String(segments[0]?.path ?? '').toLowerCase();
  if (!value) return false;
  return (site.config().supportedLocales ?? []).some((locale) => [
    locale.code,
    locale.pathPrefix,
    ...(locale.aliases ?? [])
  ].some((candidate) => String(candidate ?? '').toLowerCase() === value));
};
